import React from 'react';
import i18n from 'i18n-js';
import PropTypes from 'prop-types';

import { PowerSettingsNew, SensorDoor, OfflineBolt, LocalGasStation } from '@mui/icons-material';

import { DatetimeField } from './Telemetry';

import ChargeStateIcon from 'assets/img/icons/charge_state.svg';
import IsChargingIcon from 'assets/img/icons/is_charging.svg';
import Odometer from 'assets/img/icons/gauge.svg';
import {
  Card,
  Box,
  Grid,
  List,
  Button,
  SvgIcon,
  ListItem,
  Typography,
  styled,
} from '@mui/material';

const OdometerText = styled('span')(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  marginLeft: theme.spacing(1),
}));

const StyledList = styled(List)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'flex-start',
  padding: 0,
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  width: '100%',
  padding: '10px 4px 8px 0',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`, // using grey[300] as approximation for common.greyLight or check theme
  },
  '&:last-child': {
    paddingBottom: 0,
  },
}));

const UpdateButton = styled(Button)({
  textTransform: 'capitalize',
});

const TelemetryStats = ({ telemetry, timezone, localeFormat, onUpdate }) => {
  const statusList = [
    {
      label: `${i18n.t('telemetry.power_status')}:`,
      key: 'powerStatus',
      icon: PowerSettingsNew,
      color: '#F74359',
      iconType: 'icon',
      show: true,
    },
    {
      label: `${i18n.t('telemetry.door_status')}:`,
      key: 'doorStatus',
      icon: SensorDoor,
      color: '#B366CE',
      iconType: 'icon',
      show: true,
    },
  ];

  const vitalsList = [
    {
      label: `${i18n.t('telemetry.battery')}:`,
      key: 'battery',
      icon: OfflineBolt,
      color: '#19A593',
      iconType: 'icon',
      show: telemetry?.batt_lv?.magnitude ? true : false,
    },
    {
      label: (
        <Box minWidth="80%">
          {i18n.t('telemetry.odometer')}:
          {telemetry?.odometer_method && (
            <OdometerText>
              {i18n.t(`telemetry.odometer_method.${telemetry?.odometer_method}`, {
                defaultValue: telemetry?.odometer_method,
              })}
            </OdometerText>
          )}
        </Box>
      ),
      key: 'odometer',
      icon: Odometer,
      iconType: 'img',
      show: telemetry?.odometer?.magnitude ? true : false,
    },
  ];

  const powerList = [
    {
      label: `${i18n.t('telemetry.fuel')}:`,
      key: 'fuel',
      icon: LocalGasStation,
      color: '#FF8A00',
      iconType: 'icon',
      show: telemetry?.fuel_level?.magnitude ? true : false,
    },
    {
      label: `${i18n.t('telemetry.is_charging')}:`,
      key: 'is_charging',
      icon: IsChargingIcon,
      iconType: 'img',
      show: telemetry?.is_charging != null,
    },
    {
      label: `${i18n.t('telemetry.charge_level')}:`,
      key: 'charge',
      icon: ChargeStateIcon,
      iconType: 'img',
      show: telemetry?.charge_level?.magnitude ? true : false,
    },
  ];

  const showStatusList = telemetry?.power_status || telemetry?.is_locked ? true : false;

  const showVitalsList =
    telemetry?.odometer?.magnitude || telemetry?.batt_lv?.magnitude ? true : false;

  const showPowerList =
    telemetry?.fuel_level?.magnitude || telemetry?.charge_level?.magnitude || telemetry?.is_charging
      ? true
      : false;

  const powerTime = telemetry?.charge_level_time || telemetry?.capture_time;

  /**
   * Render telemetry stat item
   * @param {object} stat Object with telemetry stat config
   */
  const TelemetryItem = ({ stat }) => {
    const batteryUnit = telemetry?.batt_lv?.units || '';
    const batteryValue = telemetry?.batt_lv?.magnitude?.toString() || '';
    const chargeValue = telemetry?.charge_level?.magnitude?.toString() || '';
    const chargeUnit = telemetry?.charge_level?.units || '';
    const isChargingValue = telemetry?.is_charging || false;
    const odometerUnit = telemetry?.odometer?.units || '';
    const odometerValue = telemetry?.odometer?.magnitude.toString() || '';

    const telemetryStats = {
      powerStatus: telemetry?.power_status || 'OFF',
      battery: batteryValue ? `${batteryValue} ${batteryUnit}` : '',
      charge: chargeValue ? `${chargeValue} ${chargeUnit}` : '',
      is_charging: isChargingValue ? i18n.t('common.yes') : i18n.t('common.no'),
      odometer: (
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <span>{odometerValue ? `${odometerValue}${odometerUnit}` : ''}</span>
          </Box>
        </Box>
      ),
      doorStatus:
        telemetry?.is_locked !== null ? (telemetry?.is_locked ? 'Locked' : 'Unlocked') : '',
      fuel: telemetry?.fuel_level?.magnitude ? telemetry?.fuel_level?.magnitude + ' %' : '',
    };

    if (!telemetryStats?.[stat.key]) return null;

    return (
      <Grid container direction="row" alignItems="center">
        <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
          <Box display="flex" alignItems="center">
            {stat.iconType === 'img' ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ height: 24, width: 24, marginRight: '10px' }}
              >
                <img src={stat.icon} alt={stat.key} />
              </Box>
            ) : (
              <SvgIcon
                component={stat.icon}
                sx={{
                  color: stat.color,
                  marginRight: '10px',
                }}
              />
            )}
            <Typography component="div">
              <Box fontSize={13} color="#4D4F5C" fontWeight="bold">
                {stat.label}
              </Box>
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 4 }} sx={{ p: 1 }}>
          <Typography component="div">
            <Box
              fontSize={13}
              display="flex"
              justifyContent="flex-end"
              data-cy={`${stat.key}-value`}
            >
              {telemetryStats?.[stat.key]}
            </Box>
          </Typography>
        </Grid>
      </Grid>
    );
  };

  TelemetryItem.propTypes = {
    stat: PropTypes.object,
  };

  return (
    <Box>
      {showStatusList && (
        <Card sx={{ marginBottom: '20px', p: 1, pb: 2 }}>
          <Grid container>
            <Grid size={{ xs: 4 }} sx={{ p: 1 }} data-cy="status-title">
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontWeight: 500 }}>
                {i18n.t('telemetry.status')}
              </Typography>
            </Grid>

            {telemetry?.capture_time && (
              <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                <Typography variant="caption">
                  <Box>
                    {i18n.t('telemetry.time_label')}{' '}
                    <DatetimeField
                      timezone={timezone}
                      localeFormat={localeFormat}
                      date={telemetry?.capture_time}
                    />
                  </Box>
                </Typography>
              </Grid>
            )}
          </Grid>

          <StyledList>
            {statusList.map((val, index) => {
              if (!val.show) return null;
              return (
                <StyledListItem key={index}>
                  <TelemetryItem key={index} stat={val} />
                </StyledListItem>
              );
            })}
          </StyledList>
        </Card>
      )}

      {showVitalsList && (
        <Card sx={{ marginBottom: '20px', p: 1 }}>
          <Grid container>
            <Grid size={{ xs: 4 }} sx={{ p: 1 }} data-cy="vitals-title">
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontWeight: 500 }}>
                {i18n.t('telemetry.vitals')}
              </Typography>
            </Grid>

            {telemetry?.odometer_time && (
              <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                <Typography variant="caption">
                  <Box>
                    {i18n.t('telemetry.time_label')}{' '}
                    <DatetimeField
                      timezone={timezone}
                      localeFormat={localeFormat}
                      date={telemetry?.odometer_time}
                    />
                  </Box>
                </Typography>
              </Grid>
            )}
          </Grid>
          <StyledList>
            <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
              {vitalsList.map((val, index) => {
                if (!val.show) return null;
                return (
                  <StyledListItem key={index}>
                    <TelemetryItem key={index} stat={val} />
                  </StyledListItem>
                );
              })}
              {telemetry?.odometer_method === 'DISTANCE_SINCE_CLEARANCE' && (
                <UpdateButton color="primary" onClick={onUpdate} size="small">
                  {i18n.t('common.update')}
                </UpdateButton>
              )}
            </Box>
          </StyledList>
        </Card>
      )}

      {showPowerList && (
        <Card sx={{ marginBottom: '20px', p: 1 }}>
          <Grid container>
            <Grid size={{ xs: 4 }} sx={{ p: 1 }} data-cy="power-title">
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontWeight: 500 }}>
                {i18n.t('telemetry.power_status')}
              </Typography>
            </Grid>

            {powerTime && (
              <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                <Typography variant="caption">
                  <Box>
                    {i18n.t('telemetry.time_label')}{' '}
                    <DatetimeField
                      timezone={timezone}
                      localeFormat={localeFormat}
                      date={powerTime}
                    />
                  </Box>
                </Typography>
              </Grid>
            )}
          </Grid>

          <StyledList>
            <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
              {powerList.map((val, index) => {
                if (!val.show) return null;
                return (
                  <StyledListItem key={index}>
                    <TelemetryItem key={index} stat={val} />
                  </StyledListItem>
                );
              })}
            </Box>
          </StyledList>
        </Card>
      )}
    </Box>
  );
};

TelemetryStats.propTypes = {
  telemetry: PropTypes.object,
  timezone: PropTypes.string,
  localeFormat: PropTypes.string,
  onUpdate: PropTypes.func,
};

export default TelemetryStats;
